import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import cx from 'classnames';

import {
  CryptoLabelPill,
  LangSwitcher,
  Loader,
  RiskIndicatorLimited,
  SvgIcon,
  Button,
} from '@/components';
import { getIconByNetwork, setWidgetCookie, getWidgetCookie } from '@/helpers';
import { useApiCall } from '@/hooks';

import amlbotLogo from '@/assets/images/logo-amlbot.svg';
import bgImg from '@/assets/images/ref-signup/bg.svg';
import filecheckImg from '@/assets/icons/filepcheck.svg';

import { LandingFooter } from '@/components/LandingFooter/LandingFooter';
import {
  ChainsEnum,
  ChainsDictionary,
  type WidgetUser,
  type WidgetCacheEntry,
} from '@/config';
import {
  AmlPreviewData,
  amlbotAPI,
  AmlPreviewResponse,
  AmlPreviewRequest,
  AmlFlowEnum,
  WidgetUserRequest,
  WidgetUserResponse,
} from '@/api';
import { SignUpForm } from '../SignUp/components/SignUpForm/SignUpForm';
import { AddressConnections } from './components';
import { userSelectors } from '@/redux/selectors';
import { useAppSelector } from '@/redux/hooks';
import { useRefSignUpPartner } from './hooks/useRefSignUpPartner';
import { useAmlBotWidget } from './hooks/useAmlBotWidget';
import { DEFAULT_WIDGET_APP_ID } from './RefSignUp.constants';

import styles from './RefSignUp.module.sass';

export const RefSignUp = () => {
  const isAuth = useAppSelector(userSelectors.getAuth);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { logo: partnerLogo, label: partnerLabel } = useRefSignUpPartner();
  const [amlPreviewData, setAmlPreviewData] = useState<AmlPreviewData>();
  const [amlPreviewDataFetched, setAmlPreviewDataFetched] = useState(false);
  const [widgetAppId, setWidgetAppId] = useState<string | null>(null);
  const [widgetUser, setWidgetUser] = useState<WidgetUser | null>(null);
  const [userFetched, setUserFetched] = useState(false);
  const [widgetUserFailed, setWidgetUserFailed] = useState(false);
  const [widgetVisible, setWidgetVisible] = useState(false);

  const ref = searchParams.get('ref') || '';
  const widget = useAmlBotWidget({ appId: widgetAppId, user: widgetUser });

  const [getAmlPreviewData, amlLoading, amlError] = useApiCall(async () => {
    const payload: AmlPreviewRequest = {
      ref,
      uid: searchParams.get('hash') || '',
    };
    try {
      const response: AmlPreviewResponse =
        await amlbotAPI.fetchAmlPreview(payload);

      if (response.success) {
        const data: AmlPreviewData = {
          uid: response.data.uid,
          address: response.data.hash,
          network: response.data.network.toUpperCase() as ChainsEnum,
          riskScoreLevel: response.data.riskScoreLevel,
          blocklisted: response.data.blocklisted,
          exposures: response.data.exposures,
        };

        setAmlPreviewData(data);
      }
    } finally {
      setAmlPreviewDataFetched(() => true);
    }
  });

  const [registerWidgetUser] = useApiCall(async () => {
    try {
      const cached = getWidgetCookie(ref);

      if (cached) {
        setWidgetAppId(() => cached.appId);
        setWidgetUser(() => cached.user);
        return;
      }

      const payload: WidgetUserRequest = { ref };
      const response: WidgetUserResponse =
        await amlbotAPI.registerWidgetUser(payload);

      if (!response.success) {
        setWidgetUserFailed(() => true);
        return;
      }

      const entry: WidgetCacheEntry = {
        appId: response.data.appId || DEFAULT_WIDGET_APP_ID,
        user: {
          username: response.data.username,
          externalId: response.data.uid,
        },
      };

      setWidgetAppId(() => entry.appId);
      setWidgetUser(() => entry.user);
      setWidgetCookie(ref, entry);
    } catch (error) {
      setWidgetUserFailed(() => true);
      throw error;
    } finally {
      setWidgetAppId((current) => current || DEFAULT_WIDGET_APP_ID);
      setUserFetched(() => true);
    }
  });

  const hasInfo = !!amlPreviewData?.uid;
  const amlFetchFailed = amlPreviewDataFetched && !amlPreviewData;
  // Registration finished but produced no appId — not even the default one is
  // configured, so there is nothing to mount the widget with.
  const widgetUnavailable = userFetched && !widgetAppId;
  const widgetFailed =
    widget.loadFailed || widgetUserFailed || widgetUnavailable;
  const showWidget = amlPreviewData && widget.isReady() && widgetUser;
  const showFallback =
    !showWidget && (!widget.isLoading || widgetFailed || amlFetchFailed);
  const showWidgetPending = !showWidget && !showFallback;

  useEffect(() => {
    if (amlPreviewData || amlPreviewDataFetched) return;

    getAmlPreviewData();
  }, [amlPreviewData, amlPreviewDataFetched]);

  useEffect(() => {
    if (amlPreviewData && amlPreviewDataFetched && widget && !userFetched) {
      registerWidgetUser();
    }
  }, [amlPreviewData, amlPreviewDataFetched, widget, userFetched]);

  const sendToWidget = () => {
    if (amlPreviewData) {
      setWidgetVisible(true);
      widget.sendData({
        hash: amlPreviewData.address,
        network: amlPreviewData.network,
      });
    }
  };

  const classes = cx(styles.refSignUp, {
    [styles['has-info']]: hasInfo,
  });

  if (isAuth) {
    return <Navigate to="/" />;
  }

  if (amlLoading) {
    return <Loader />;
  }

  return (
    <div
      className={classes}
      style={
        {
          '--bg-image': `url(${bgImg})`,
        } as CSSProperties
      }
    >
      <header className={styles.header}>
        <Link to="/" className={styles.logo}>
          <img src={amlbotLogo} alt="AMLBot" />
        </Link>

        <div className={styles.actions}>
          <Link to="/signin" className={styles.signInLink}>
            {t('translation.sign_in')}
          </Link>

          {partnerLogo && (
            <div className={styles.partner}>
              {t('translation.partnered_with')}{' '}
              <img src={partnerLogo} alt={partnerLabel || ''} />
            </div>
          )}

          <LangSwitcher
            fullLabel={false}
            className={styles.langSwitcher}
            isRounded
          />
        </div>
      </header>

      <div
        className={`${styles.content} ${widgetVisible ? styles.shrinked : ''}`}
      >
        <div
          style={{ display: widgetVisible ? 'block' : 'none' }}
          id="amlbot-inline-container"
          className={styles.inlineWidget}
        />

        {!widgetVisible && (
          <>
            {hasInfo && (
              <div className={styles.info}>
                <div className={styles.title}>
                  {t('translation.refSignUp.amlRiskResult')}
                </div>

                <CryptoLabelPill
                  iconUrl={getIconByNetwork(amlPreviewData?.network)}
                  label={ChainsDictionary[amlPreviewData?.network]?.name || ''}
                  protocol=""
                  abbr={ChainsDictionary[amlPreviewData?.network]?.symbol || ''}
                />

                <div className={styles.address}>
                  {amlPreviewData?.address || ''}
                </div>

                {!amlPreviewData?.blocklisted && (
                  <div className={styles.blacklistFree}>
                    <img src={filecheckImg} alt="" />
                    {t('translation.blacklist_free')}
                  </div>
                )}

                {amlPreviewData?.riskScoreLevel && (
                  <RiskIndicatorLimited
                    amlFlow={AmlFlowEnum.LITE}
                    value={amlPreviewData?.riskScoreLevel}
                  />
                )}

                {!!amlPreviewData?.exposures && (
                  <AddressConnections
                    low={amlPreviewData?.exposures?.low}
                    medium={amlPreviewData?.exposures?.medium}
                    high={amlPreviewData?.exposures?.high}
                  />
                )}
              </div>
            )}

            <div className={styles.signIn}>
              {showWidgetPending && <Loader />}

              {showWidget && (
                <Button
                  size="xl"
                  corner="rect"
                  onClick={sendToWidget}
                  disabled={amlLoading}
                  fullwidth
                >
                  {t('translation.widget_check_btn')}
                </Button>
              )}

              {showFallback && (
                <>
                  <div className={styles.lock}>
                    <SvgIcon name="lock" />
                  </div>

                  <div className={styles.heading}>
                    <div className={styles.title}>
                      {t('translation.refSignUp.toContinue')}
                    </div>

                    {!hasInfo && (
                      <div className={styles.freeCheck}>
                        {t('translation.refSignUp.signUpAndGet')}
                        <span className={styles.badge}>
                          <SvgIcon name="aml" /> 1
                        </span>
                        {t('translation.refSignUp.freeCheck')}
                      </div>
                    )}

                    <div className={styles.description}>
                      {t('translation.already_have_one')}{' '}
                      <Link to="/signin" className={styles.link}>
                        {t('translation.sign_in')}
                      </Link>
                    </div>
                  </div>

                  {(widgetFailed || amlFetchFailed) && (
                    <SignUpForm
                      amlPreviewData={amlPreviewData}
                      hidePasswordConfirmation
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <LandingFooter type="white" />
    </div>
  );
};

export default RefSignUp;
